import React, {useState, useEffect} from "react";
import {
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Box,
    useMediaQuery,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useTheme} from "@mui/material/styles";
import {
    getPrediccionHandicapCombinado,
    PrediccionHandicapResponse,

} from "../../../services/hipodromo";
import {useParams} from "react-router-dom"; // Asegúrate que la ruta a hipodromo.ts sea correcta
import {getColors} from "../../../componetes/color_racers";
import MinimalHandicapChart from "./MinimalHandicapChart";

// Componente principal
const PredicHandicapView: React.FC<{ raceId?: number }> = ({ raceId }) => {
    const {proCarIdHipodromo, id, hipodromoId} = useParams<{ proCarIdHipodromo?: string, id?: string, hipodromoId?: string }>();
    const [data, setData] = useState<PrediccionHandicapResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const raceIdToUse = raceId || Number(proCarIdHipodromo) || Number(id);
            if (!raceIdToUse) return;

            setLoading(true);
            setError(null);
            try {
                const hId = hipodromoId ? Number(hipodromoId) : 1;
                const response = await getPrediccionHandicapCombinado(raceIdToUse, hId);
                setData(response);
                if (response && response.predicciones && response.ranking_general) {
                    const initialExpandedIds = new Set(response.participantes_resumen.map(p => p.ejemplar_id));
                    setExpandedAccordionIds(initialExpandedIds);
                }
            } catch (err: any) {
                setError(err.message || "Ocurrió un error desconocido");
                console.error("Error en PredicHandicapView:", err); // Log detallado en consola
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [proCarIdHipodromo, id, raceId, hipodromoId]);

    const { ejemplares, predValues, rankValues } = React.useMemo(() => {
        if (!data || !data.predicciones || !data.ranking_general) {
            return { ejemplares: [], predValues: [], rankValues: [] };
        }

        // Combinar y ordenar datos por número de inscripción para asegurar la correspondencia
        const combinedData = new Map();

        data.predicciones.forEach(p => {
            const inscripcion = p.pro_car_inscripcion;
            if (!combinedData.has(inscripcion)) {
                combinedData.set(inscripcion, {
                    inscripcion_numero: inscripcion,
                    predition: null,
                    ranking_score: null
                });
            }
            combinedData.get(inscripcion).predition = +(Number(p.pro_car_predition) * 100).toFixed(2);
        });

        data.ranking_general.forEach(r => {
            const inscripcion = r.inscripcion_numero;
            if (!combinedData.has(inscripcion)) {
                combinedData.set(inscripcion, {
                    inscripcion_numero: inscripcion,
                    predition: null,
                    ranking_score: null
                });
            }
            combinedData.get(inscripcion).ranking_score = r.ranking_score;
        });

        const sortedCombinedData = Array.from(combinedData.values()).sort((a, b) => a.inscripcion_numero - b.inscripcion_numero);

        const ejemplares = sortedCombinedData.map(item => item.inscripcion_numero.toString());
        const predValues = sortedCombinedData.map(item => item.predition !== null ? item.predition : 0); // Manejar predicciones faltantes
        const rankValues = sortedCombinedData.map(item => item.ranking_score !== null ? item.ranking_score : 0); // Manejar ranking faltante

        return { ejemplares, predValues, rankValues };
    }, [data]);

    console.log(data);
    const [expandedAccordionIds, setExpandedAccordionIds] = useState<Set<number>>(new Set()); // Estado para los acordeones expandidos

    const handleAccordionChange = (id: number) => {
        setExpandedAccordionIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


    // Renderizado condicional basado en el estado


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress/>
            </Box>
        );
    }

    if (error) {
        // Muestra el mensaje de error (incluyendo el de suscripción Premium)
        return <Alert severity="error">{error}</Alert>;
    }

    if (!data || !data.predicciones || !data.ranking_general) {
        // Caso improbable si la API devuelve 200 pero sin datos esenciales
        return <Alert severity="warning">No se encontraron datos completos para mostrar.</Alert>;
    }

    const MobileView = ({
                            expandedAccordionIds,
                            handleAccordionChange,
                            ejemplares,
                            predValues,
                            rankValues
                        }: {
        expandedAccordionIds: Set<number>,
        handleAccordionChange: (id: number) => void,
        ejemplares: string[],
        predValues: number[],
        rankValues: number[]
    }) => (
        <div style={{padding: 16}}>
            <Typography variant="h5" gutterBottom>
                Predicción vs Ranking
            </Typography>

            <Card>
                <CardContent sx={{p: 0}}>
                    <MinimalHandicapChart
                        ejemplares={ejemplares}
                        predValues={predValues}
                        rankingScores={rankValues}
                    />
                </CardContent>
            </Card>

            <Card sx={{mt: 2, mb: 3}}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Resumen de Participantes</Typography>
                    {data.participantes_resumen.sort((a, b) => a.inscripcion_numero - b.inscripcion_numero).map(p => (
                        <Card key={p.ejemplar_id} sx={{
                            mb: 2,
                        }}>
                            <CardContent>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        color: getColors(p.inscripcion_numero).textColor,
                                        backgroundColor: getColors(p.inscripcion_numero).color,
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        display: 'inline-block',
                                        mb: 1
                                    }}
                                >
                                    {p.inscripcion_numero}. {p.ejemplar_name}
                                </Typography>
                                <Accordion expanded={expandedAccordionIds.has(p.ejemplar_id)}
                                           onChange={() => handleAccordionChange(p.ejemplar_id)}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon/>}
                                        aria-controls={`panel-${p.ejemplar_id}-content`}
                                        id={`panel-${p.ejemplar_id}-header`}
                                    >
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2"><strong>Jinete:</strong> {p.jinete_name}
                                        </Typography>
                                        <Typography variant="body2"><strong>Preparador:</strong> {p.preparador_name}
                                        </Typography>
                                        <Typography variant="body2"><strong>Peso
                                            Jinete:</strong> {p.peso_jinete_proximo} kg</Typography>
                                        <Typography variant="body2"><strong>Índice
                                            Prom.:</strong> {p.indice_promedio ?? 'N/A'}</Typography>
                                        <Typography variant="body2"><strong>Vel.
                                            Prom.:</strong> {p.velocidad_promedio_ms ?? 'N/A'} m/s</Typography>
                                        <Typography variant="body2"><strong>Peso Total
                                            Prom.:</strong> {p.peso_total_promedio_kg ?? 'N/A'} kg</Typography>
                                        <Typography variant="body2"><strong>Resultados Pts.
                                            Prom.:</strong> {p.resultado_pts_promedio ?? 'N/A'}</Typography>
                                        <Typography variant="body2"><strong>Carreras
                                            Consideradas:</strong> {p.carreras_consideradas ?? 'N/A'}</Typography>
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                        </Card>
                    ))}
                </CardContent>
            </Card>


        </div>
    );

    const DesktopView = ({ejemplares, predValues, rankValues}: {
        ejemplares: string[],
        predValues: number[],
        rankValues: number[]
    }) => (
        <Box sx={{padding: 2}}>
            <Typography variant="h5" gutterBottom>
                Predicción vs Ranking
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent sx={{p: 0}}>
                            <MinimalHandicapChart
                                ejemplares={ejemplares}
                                predValues={predValues}
                                rankingScores={rankValues}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Resumen de Participantes</Typography>
                            <TableContainer component={Paper} sx={{mb: 3}}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Inscripción</TableCell>
                                            <TableCell>Ejemplar</TableCell>
                                            <TableCell align="right">Índice Prom.</TableCell>
                                            <TableCell align="right">Vel. Prom. (m/s)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.participantes_resumen.sort((a, b) => a.inscripcion_numero - b.inscripcion_numero).map(p => (
                                            <TableRow key={p.ejemplar_id}>
                                                <TableCell component="th" scope="row">
                                                    <Chip
                                                        label={`${p.inscripcion_numero}.`}
                                                        sx={{
                                                            backgroundColor: getColors(p.inscripcion_numero).color,
                                                            color: getColors(p.inscripcion_numero).textColor,
                                                            fontWeight: 'bold',
                                                        }}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{p.ejemplar_name}</TableCell>
                                                <TableCell align="right">{p.indice_promedio ?? 'N/A'}</TableCell>
                                                <TableCell align="right">{p.velocidad_promedio_ms ?? 'N/A'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );

    return isMobile ?
        <MobileView expandedAccordionIds={expandedAccordionIds} handleAccordionChange={handleAccordionChange}
                    ejemplares={ejemplares} predValues={predValues} rankValues={rankValues}/> :
        <DesktopView ejemplares={ejemplares} predValues={predValues} rankValues={rankValues}/>;
};

export default PredicHandicapView; // Exporta el componente renombrado o el original